import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { TCustomError } from '~/api/middlewares/errorHandler'

interface IAxiosClient extends AxiosInstance {
  request<T>(config: AxiosRequestConfig): Promise<T>
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>
  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T>
  head<T>(url: string, config?: AxiosRequestConfig): Promise<T>
  options<T>(url: string, config?: AxiosRequestConfig): Promise<T>
  post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>
  put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>
  patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>
}

/**
 * 확장된 Axios 에러 인터페이스
 * @template T 응답 데이터 타입 (에러 응답의 body)
 * @template D 요청 데이터 타입 (config에서 사용)
 */
export interface IAxiosClientError<T = TCustomError, D = any> extends AxiosError {
  response: AxiosResponse<T, D>
}

class AxiosClient {
  readonly instance: IAxiosClient

  constructor(req?: any) {
    const isServer = typeof window === 'undefined'
    const host = isServer ? `http://localhost:3000` : ''
    const headers = req ? req.headers : {}

    this.instance = axios.create({
      baseURL: host,
      headers: {
        ...headers,
        Accept: 'application/json',
      },
      withCredentials: true,
    })

    this._initializeInterceptor()
  }

  private _initializeInterceptor = () => {
    this.instance.interceptors.response.use(this._handleResponse, this._handleError)
  }

  private _handleResponse = ({ data }: AxiosResponse) => {
    return data
  }
  protected _handleError = (error: any) => {
    // axios request fail 시 response 를 받아오지 못하는 경우,
    // response 에 dummy data 를 넣어서 error.response 를 생성해준다.
    // 이는 에러 핸들링시 불필요한 분기처리를 줄이기 위함이다.
    if (!error.response) {
      return Promise.reject({
        ...error,
        response: {
          data: '',
          status: 500,
          statusText: 'AXIOS_REQUEST_FAIL',
          headers: {},
          config: {},
        },
      })
    }

    return Promise.reject(error)
  }
}

export default AxiosClient
